// SCN Dispatch voice path — Project Rubidium (v3 protocol).
//
// Replaces the old MediaRecorder/decodeAudioData voice plumbing with IMBE
// frames carried over a Socket.IO `v3_packet` event. Multiple voice channels
// can be live at once; each gets its own VocoderDecoder. A single shared
// AudioContext is used so all decoder output sums into the same destination.
//
// Wire format (v3_packet):
//   { streamId, seq, ciphertext: Uint8Array(27), pcm?: Uint8Array(960),
//     subscriberID, alias }
//   - streamId: UUID v4, fresh per PTT-down
//   - seq: first-frame seq within the packet (increments by 3 per packet)
//   - ciphertext: 3 × 9-byte AES-256-CTR IMBE frames concatenated
//   - pcm: optional 8 kHz / 16-bit / mono PCM, 480 samples = 960 bytes
//
// Encryption: per-channel key supplied by webradio.js via setEncryptionKey()
// before the channel is joined. SHA-256(humanKey) -> 32 bytes, stored in
// `_keyMap` keyed by the full `vc[systemID:channelID]` string. Empty/null
// human keys map to 32 zero bytes (clear-call parity with SCNP25). Keys are
// never embedded in the channel name string or in any wire payload.

class VoiceV3 {
  constructor(socket) {
    // Shared graph nodes for legacy parity. compressor stays disconnected on
    // purpose — keeping the parity finding from the SCNP25 cutover.
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 48000,
      latencyHint: "interactive"
    });
    this.filter1 = this.audioContext.createBiquadFilter();
    this.filter2 = this.audioContext.createBiquadFilter();
    this.compressor = this.audioContext.createDynamicsCompressor();
    this.gainNode = this.audioContext.createGain();

    // Voice state.
    this.cbVoiceMetadata = undefined;
    this.currentChannel = undefined;
    this.currentVolume = 1;
    this.gainNode.gain.value = this.currentVolume / 1.5;
    this.voiceSubID = undefined;
    this.alias = undefined;

    // Master output bus. The gainNode is the single fader for everything the
    // dispatch's voice path emits — all decoders route their output through it
    // (see _spinupDecoder). By default it feeds audioContext.destination (the
    // system default device). When setAudioDevice() is given a non-default
    // device, we lazily build a MediaStreamAudioDestinationNode + <audio>
    // element and use setSinkId() to route the stream to the chosen output.
    this._attachMasterOutput();

    // PTT / encoder state.
    this.socket = socket;
    this.pttDown = false;
    this.encoder = undefined;
    this._frameBuffer = [];          // up to 3 9-byte frames waiting on packetisation
    this._pcmBuffer = [];            // up to 3 Float32Array(160) for the same packet
    this._packetIndex = 0;           // packets sent during current PTT
    this._currentStreamId = undefined;
    this._exposePCMForCurrentTx = false;
    this._autogain = true;           // dispatch default; override via setAutogain

    // Multi-channel decoder map: channel name (vc[...]) -> VocoderDecoder.
    this.decoders = new Map();
    // Track active stream id per channel so we can route metadata callbacks.
    this._channelMeta = new Map();   // channel -> { subscriberID, alias }
    // Per-channel SHA-256 key bytes, keyed by the full `vc[systemID:channelID]`
    // string. Populated by setEncryptionKey() before the channel is joined so
    // decoder/encoder construction can read synchronously.
    this._keyMap = new Map();        // channel -> Uint8Array(32)

    console.info("[VOICE] Init voice (Rubidium v3)");

    this.socket.on("v3_packet", (channel, pkt) => {
      this._handleV3Packet(channel, pkt);
    });
  }

  // Build the master output bus. Default routing is gainNode -> destination.
  // setAudioDevice() will rewire the master gain node to a stream destination
  // when a non-default sink is chosen.
  _attachMasterOutput() {
    this.gainNode.connect(this.audioContext.destination);
    this._currentSink = "default"; // 'default' | 'custom'
    // Lazily-created machinery for non-default sinks.
    this._streamDest = null;
    this._sinkAudio = null;
  }

  // Resume the AudioContext if it was created before any user gesture. Safe
  // to call repeatedly. Returns a promise that resolves once the context is
  // running (or fails silently if resume rejects).
  _ensureAudioContextRunning() {
    if (this.audioContext.state === "suspended") {
      return this.audioContext.resume().catch(() => {});
    }
    return Promise.resolve();
  }

  // Set the master output volume. Drives gainNode.gain directly so the change
  // is live across all currently-decoding channels (no per-decoder snapshot).
  setVolume(value) {
    const v = Number(value);
    if (!Number.isFinite(v)) return;
    this.currentVolume = v;
    this.gainNode.gain.value = v / 1.5;
  }

  async setAudioDevice(device) {
    console.debug("Set audio device", device);
    if (!device || device === null || !device.deviceId || device.deviceId === "default") {
      // Switch back to the system default output. Tear down stream routing so
      // gainNode feeds audioContext.destination directly.
      try { this.gainNode.disconnect(); } catch (e) {}
      this.gainNode.connect(this.audioContext.destination);
      if (this._sinkAudio) {
        try { this._sinkAudio.pause(); } catch (e) {}
      }
      this._currentSink = "default";
      return;
    }
    // Custom sink: gainNode -> MediaStreamDestination -> <audio> with setSinkId.
    if (!this._streamDest) {
      this._streamDest = this.audioContext.createMediaStreamDestination();
    }
    if (!this._sinkAudio) {
      this._sinkAudio = new Audio();
      this._sinkAudio.autoplay = true;
    }
    try { this.gainNode.disconnect(); } catch (e) {}
    this.gainNode.connect(this._streamDest);
    this._sinkAudio.srcObject = this._streamDest.stream;
    try { await this._sinkAudio.setSinkId(device.deviceId); } catch (e) {
      console.warn("[VOICE] setSinkId failed", e);
    }
    try { await this._sinkAudio.play(); } catch (e) {
      console.warn("[VOICE] sink audio play failed (gesture required?)", e);
    }
    this._currentSink = "custom";
  }

  setSubscriber(subscriberID) {
    this.voiceSubID = subscriberID;
  }

  setAlias(alias) {
    this.alias = alias;
  }

  setAutogain(value) {
    this._autogain = !!value;
  }

  // Allow webradio to opt the next transmission into PCM exposure when the
  // CHANNEL_GRANT advertised accessibility listeners on the talkgroup.
  setAccessibilityListenersPresent(value) {
    this._exposePCMForCurrentTx = !!value;
  }

  // --- per-channel key registration ---------------------------------------
  // Called by webradio.js with the channel string in its new keyless form
  // `vc[systemID:channelID]` (or the raw `systemID:channelID` inner — both are
  // accepted) and the human-readable per-channel key. SHA-256 derives 32 bytes
  // which are cached in `_keyMap` so spin-up/encoder construction can read
  // synchronously. Empty/null key => 32 zero bytes (parity with SCNP25).
  async setEncryptionKey(channelString, keyString) {
    if (typeof channelString !== "string" || channelString.length === 0) return;
    const channel = channelString.startsWith("vc[")
      ? channelString
      : "vc[" + channelString + "]";
    // Store the Promise immediately so any caller (decoder spinup, encoder
    // start) that fires before SHA-256 resolves can await it instead of
    // racing past with the all-zeros fallback. Once resolved, swap in the
    // bytes so subsequent lookups skip the Promise machinery.
    const promise = VoiceV3._deriveKeyBytes(keyString);
    this._keyMap.set(channel, promise);
    const keyBytes = await promise;
    if (this._keyMap.get(channel) === promise) {
      this._keyMap.set(channel, keyBytes);
    }
  }

  static async _deriveKeyBytes(humanKey) {
    if (humanKey === null || humanKey === undefined || humanKey === "" || humanKey === false) {
      // No key: clear-call, all-zero key (parity with SCNP25).
      return new Uint8Array(32);
    }
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(String(humanKey))
    );
    return new Uint8Array(buf);
  }

  async _keyForChannel(channelString) {
    const k = this._keyMap.get(channelString);
    if (k instanceof Promise) return await k;
    if (k) return k;
    // Channel was joined without a prior setEncryptionKey() call. Fall back to
    // a clear-call zero key so we don't crash; webradio.js should always set
    // the key first, but a missing call shouldn't take voice down.
    return new Uint8Array(32);
  }

  // --- voice channel join / leave -----------------------------------------
  setVoiceChannel(newChannel) {
    if (this.currentChannel !== undefined) {
      this._teardownDecoder(this.currentChannel);
      this.socket.emit("vc_leave", this.currentChannel);
      this.currentChannel = undefined;
    }
    if (newChannel != undefined) {
      this.pttDown = false;
      this.currentChannel = "vc[" + newChannel + "]";
      this.socket.emit("vc_join", this.currentChannel);
      this._ensureAudioContextRunning();
      this._spinupDecoder(this.currentChannel).catch(e =>
        console.error("[VOICE] decoder spinup failed", e)
      );
    }
  }

  leaveVoiceChannel() {
    this.setVoiceChannel(null);
  }

  async _spinupDecoder(channelString) {
    if (this.decoders.has(channelString)) return;
    const keyBytes = await this._keyForChannel(channelString);
    await Rubidium.ready();
    const decoder = new Rubidium.VocoderDecoder({
      audioContext: this.audioContext,
      encryption: { key: keyBytes },
      playbackLeadSec: 0.08,
      outputGain: 1.0,                 // master fader handles user volume now
      outputNode: this.gainNode        // route through the master bus
    });
    decoder.onError(err => {
      // Suppress underrun resyncs; they're informational.
      if (err && /underrun/.test(err.message || "")) return;
      console.warn("[VOICE] decoder error", err);
    });
    this.decoders.set(channelString, decoder);
  }

  _teardownDecoder(channelString) {
    const dec = this.decoders.get(channelString);
    if (dec) {
      try { dec.stop(); } catch (e) {}
      this.decoders.delete(channelString);
    }
    this._channelMeta.delete(channelString);
    // Drop the cached key for this channel; webradio.js will re-set it on
    // the next join. Stale keys are harmless but no point keeping them.
    this._keyMap.delete(channelString);
  }

  // --- inbound v3 packet --------------------------------------------------
  _handleV3Packet(channel, pkt) {
    try {
      // Defensive: first inbound packet after a join may arrive before any
      // user gesture. Resume context (no-op if already running).
      this._ensureAudioContextRunning();
      const decoder = this.decoders.get(channel);
      if (!decoder) {
        // Race / stale packet for a channel we're no longer on.
        return;
      }
      // Surface metadata to the rest of dispatch (subscriber id, alias).
      if (this.cbVoiceMetadata && pkt && (pkt.subscriberID !== undefined || pkt.alias !== undefined)) {
        try {
          this.cbVoiceMetadata({
            subscriberID: pkt.subscriberID,
            alias: pkt.alias,
            enhancedVoice: true
          });
        } catch (_) {}
      }
      const ct = pkt.ciphertext instanceof Uint8Array
        ? pkt.ciphertext
        : new Uint8Array(pkt.ciphertext);
      if (ct.length !== 27) {
        console.warn("[VOICE] v3_packet ciphertext wrong size:", ct.length);
        return;
      }
      for (let i = 0; i < 3; i++) {
        const slice = ct.subarray(i * 9, i * 9 + 9);
        decoder.feed(slice, { streamId: pkt.streamId, seq: pkt.seq + i });
      }
    } catch (e) {
      console.error("[VOICE] error handling v3 packet", e);
    }
  }

  // --- PTT / outbound -----------------------------------------------------
  async setPTT(pttValue) {
    this.pttDown = pttValue;
    if (pttValue) {
      // PTT-down is an immediate user gesture — guaranteed permitted moment
      // to resume a suspended AudioContext.
      await this._ensureAudioContextRunning();
      await this._startEncoder();
    } else {
      this._stopEncoder();
    }
  }

  async _startEncoder() {
    if (this.encoder || this.currentChannel === undefined) return;
    const keyBytes = await this._keyForChannel(this.currentChannel);
    await Rubidium.ready();

    this._frameBuffer = [];
    this._pcmBuffer = [];
    this._packetIndex = 0;
    this._currentStreamId = undefined;

    const exposePCM = this._exposePCMForCurrentTx === true;

    this.encoder = new Rubidium.VocoderEncoder({
      source: "mic",
      audioConstraints: {
        autoGainControl: this._autogain,
        googAutoGainControl: this._autogain,
        echoCancellation: false,
        noiseSuppression: false,
        channelCount: 1
      },
      encryption: { key: keyBytes },
      exposePCM: exposePCM
    });

    const channelAtStart = this.currentChannel;

    this.encoder.onError(err => console.error("[VOICE] encoder error", err));
    this.encoder.onFrame((frame, meta) => {
      if (!this.pttDown) return;
      if (this._currentStreamId === undefined) {
        this._currentStreamId = meta.streamId;
      }
      this._frameBuffer.push(frame);
      if (exposePCM && meta.pcm) this._pcmBuffer.push(meta.pcm);
      if (this._frameBuffer.length === 3) {
        this._emitPacket(channelAtStart);
      }
    });

    try {
      await this.encoder.start();
    } catch (e) {
      console.error("[VOICE] encoder start failed", e);
      this.encoder = undefined;
    }
  }

  _emitPacket(channel) {
    if (channel === undefined || this.currentChannel !== channel) {
      // Channel switched mid-flight; drop.
      this._frameBuffer = [];
      this._pcmBuffer = [];
      return;
    }
    const ciphertext = new Uint8Array(27);
    for (let i = 0; i < 3; i++) ciphertext.set(this._frameBuffer[i], i * 9);
    const seq = this._packetIndex * 3;
    const pkt = {
      streamId: this._currentStreamId,
      seq: seq,
      ciphertext: ciphertext,
      subscriberID: this.voiceSubID,
      alias: this.alias
    };
    if (this._pcmBuffer.length === 3) {
      // Concatenate three Float32Array(160) -> 480 samples, convert to
      // 16-bit signed little-endian Uint8Array(960).
      const out = new Uint8Array(960);
      const view = new DataView(out.buffer);
      let o = 0;
      for (let s = 0; s < 3; s++) {
        const block = this._pcmBuffer[s];
        for (let n = 0; n < 160; n++) {
          let v = block[n];
          if (v > 1) v = 1; else if (v < -1) v = -1;
          const i16 = v < 0 ? Math.round(v * 0x8000) : Math.round(v * 0x7FFF);
          view.setInt16(o, i16, true);
          o += 2;
        }
      }
      pkt.pcm = out;
    }
    this.socket.emit("v3_packet", channel, pkt);
    this._packetIndex++;
    this._frameBuffer = [];
    this._pcmBuffer = [];
  }

  _stopEncoder() {
    if (this.encoder) {
      try { this.encoder.stop(); } catch (e) {}
      this.encoder = undefined;
    }
    this._frameBuffer = [];
    this._pcmBuffer = [];
    this._packetIndex = 0;
    this._currentStreamId = undefined;
    // Do NOT clear _exposePCMForCurrentTx — webradio sets it per CHANNEL_GRANT.
  }
}

// Expose as global for the voice.js router
if (typeof window !== "undefined") { window.VoiceV3 = VoiceV3; }

